import type { UseRelativeTimeOptions } from '../types'
import {
    type MaybeRef,
    useClock,
    useDayjs,
    toValue,
    ref,
    useNuxtApp,
    watch,
    readonly,
    type Ref
} from '#imports'

/**
 * Get date difference compared to date in human-readable format.
 * @param date
 * @param options - Defaults: { withoutAgo = false, relativeToClock: true, once: false }
 */
export const useRelativeTime = (date: MaybeRef<ReturnType<ReturnType<typeof useDayjs>> | number | string>, options: UseRelativeTimeOptions = {}): Readonly<Ref<string | undefined>> => {
    const nuxtApp = useNuxtApp()
    const { clock } = useClock()
    const dayjs = useDayjs()

    const {
        withoutAgo = false,
        relativeToClock = true,
        once = false
    } = options

    function getRelativeTime() {
        const dateRaw = toValue(date)
        const clockRaw = toValue(clock)

        let preparedDate: ReturnType<typeof dayjs> | null
        const preparedClock = dayjs(clockRaw)

        if (typeof dateRaw === 'string' || Number.isInteger(dateRaw)) {
            preparedDate = dayjs(dateRaw).tz()
        }
        else {
            // Dayjs
            preparedDate = dateRaw as ReturnType<typeof dayjs>
        }

        if (relativeToClock) {
            if (!preparedDate) return undefined

            return preparedDate.from(preparedClock, withoutAgo)
        }

        const now = dayjs.tz()
        const result = now.to(preparedDate, withoutAgo)

        return result
    }

    const relativeTime = ref<string | undefined>(getRelativeTime())

    // Time without reactivity.
    if (once) {
        relativeTime.value = getRelativeTime()
        return readonly(relativeTime)
    }

    if (import.meta.client) {
        watch(clock, () => {
            if (nuxtApp.isHydrating) return

            relativeTime.value = getRelativeTime()
        })

        watch(() => toValue(date), () => {
            if (nuxtApp.isHydrating) return

            relativeTime.value = getRelativeTime()
        })
    }

    return readonly(relativeTime)
}
